import React from 'react'
import { Section, GuideHeader, P, UI, Bullets, Steps, Callout, Table } from './GuideParts'
import SuperAdminDefinition from '../../components/opco/admin/SuperAdminDefinition'
import OpCoRoleHelp from '../../components/opco/admin/OpCoRoleHelp'

export default function OpCoGuide() {
  return (
    <div className="space-y-8">
      <GuideHeader
        title="OpCos for Super Admins"
        intro="Everything you manage under Users & Groups > OpCos. You see all OpCos at once and decide who belongs where."
      />

      <Section number={1} title="What a Super Admin Is">
        <SuperAdminDefinition />
        <Callout kind="info">
          You always work as an Admin first. A Super Admin whose Admin role is removed loses the extra rights until it is given back.
          The last effective Super Admin cannot be removed, deactivated or demoted.
        </Callout>
      </Section>

      <Section number={2} title="Setting Up OpCos">
        <Steps>
          <li>Open <UI>Users &amp; Groups</UI> &gt; <UI>OpCos</UI>.</li>
          <li>Choose <UI>Add OpCo</UI> or <UI>Import CSV</UI>. Give each OpCo a code and a name.</li>
          <li>Use the Active / Inactive / All filter to find an OpCo. You can rename it, deactivate it and reactivate it later.</li>
          <li>Right away, give the new OpCo its NetSuite and Business Central connection if it needs one (see section 9), or its users have no ERP.</li>
        </Steps>
        <P>
          The view switch at the top of the tab has two modes: <UI>Auto-assign</UI> (the default once an OpCo exists) and
          <UI> OpCos &amp; members (manual)</UI>.
        </P>
        <P>
          Use <UI>Workspace</UI> in the top bar to enter one OpCo, or <UI>All OpCos</UI> to see everything across the organisation.
        </P>
      </Section>

      <Section number={3} title="Admins and Members">
        <Bullets>
          <li><UI>Add member</UI>, <UI>Invite people</UI> and <UI>Import members</UI> add people to an OpCo.</li>
          <li>There is one management role: an Admin is the manager of the OpCo they belong to. A row shows <UI>Admin</UI> or <UI>Member</UI>, and you change it by giving the person the Admin role on the Users page. An Admin belongs to exactly one OpCo, and only a Super Admin puts an Admin into an OpCo.</li>
          <li><UI>Set primary</UI> picks the OpCo a person lands in. A “Primary” badge marks it.</li>
          <li><UI>Remove member</UI> takes them out of that OpCo only.</li>
          <li>A warning shows when an OpCo has no Admin.</li>
        </Bullets>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <OpCoRoleHelp role="member" />
          <OpCoRoleHelp role="manager" />
        </div>
      </Section>

      <Section number={4} title="Bringing Someone’s Existing Work Into an OpCo">
        <P>
          Adding a person to an OpCo only creates a membership. What they made before stays Unassigned until you move it on purpose.
          After <UI>Add member</UI>, or with <UI>Move work</UI> on their row, the dialog “Bring &lt;person&gt;’s existing work into &lt;OpCo&gt;” opens.
        </P>
        <Steps>
          <li>Review the preview. Nothing moves until you confirm, and you can untick anything to leave it out.</li>
          <li>Choose <UI>Move</UI>. Up to 500 people’s items are handled per run.</li>
          <li>Check the <UI>Moved work</UI> list. Each run has an <UI>Undo</UI>, which only reverts items still exactly as the run left them.</li>
        </Steps>
        <Bullets>
          <li>Moved: requests the person created that have no OpCo, with their documents and the open month’s billed pages.</li>
          <li>Left in place and listed: requests that are still extracting, posted to an ERP or delivered, or whose template belongs to another OpCo.</li>
          <li>A request and its template always end up in the same OpCo. A request on an Unassigned template is only moved when that template moves in the same run; otherwise it is listed as blocked with “Its template isn’t in this OpCo yet. Assign the template first”.</li>
          <li>A template they created only moves when every request using it is already in the OpCo or moving too. Otherwise it is listed as “needs a decision”. You can include it only if no Unassigned request would be left on it; if some would, the plan shows an error and you assign those requests first.</li>
        </Bullets>
        <Callout kind="tip">
          This is deliberately not automatic: a shared template or a person in several OpCos could otherwise be pulled into the wrong OpCo.
        </Callout>
      </Section>

      <Section number={5} title="Auto-assign: Membership by Email">
        <P>
          Rules say that people with an address like <span className="font-mono">@midwich.nl</span> belong to one OpCo, so nobody adds them by hand.
        </P>
        <Steps>
          <li><span className="font-semibold">Add rules.</span> Choose <UI>Domain</UI> or <UI>One address</UI>, pick the OpCo and choose <UI>Add rule</UI>. A rule starts switched off. Free email providers and shared endings such as co.uk are refused. Sub-domains must be ticked on purpose. Use “Try an address” to see what a rule would do.</li>
          <li><span className="font-semibold">Place existing people.</span> <UI>Preview</UI> shows who has no OpCo and where the rules would put them. Nothing changes until you choose Apply. You can leave people out, <UI>Download CSV</UI>, and tick “Organisation Admins join as OpCo managers” if you want that.</li>
          <li><span className="font-semibold">Switch the rule on.</span> From then on a new person with a matching address joins as a member at their <em>first</em> sign-in only.</li>
        </Steps>
        <Bullets>
          <li>It never changes people who have already signed in, never puts back someone you removed, and skips Super Admins, guests and service accounts.</li>
          <li>Every Apply is listed under <UI>Runs</UI>, with Details and Undo. Members added this way show an “Added automatically” tag.</li>
          <li>People who only use the Outlook add-in and never open the web app are not placed automatically.</li>
        </Bullets>
        <Callout kind="warn" title="Before you switch a rule on">
          Check what a real sign-in token looks like for that domain (member or guest, which address field it carries) so the rule matches who you expect.
        </Callout>
      </Section>

      <Section number={6} title="Domains and Mailboxes">
        <P>
          In an OpCo’s <UI>Domains &amp; mailboxes</UI> list, add a mailbox (such as <span className="font-mono">orders@midwich.nl</span>) or a whole domain.
          Email and add-in requests from them are routed to that OpCo.
        </P>
      </Section>

      <Section number={7} title="Super Admins Panel">
        <Steps>
          <li>In the manual view, open the <UI>Super Admins</UI> panel.</li>
          <li>Enter the person’s email and choose <UI>Make admin</UI>. Read the list of rights in the <UI>Make Super Admin</UI> dialog and confirm.</li>
          <li>To take it away, choose <UI>Remove Super Admin</UI>.</li>
        </Steps>
        <Bullets>
          <li>The person needs the Admin role. Without it the row says “Not active: needs the Admin role”.</li>
          <li>People set in the deployment show “Permanent” and cannot be removed here. Others show who added them.</li>
          <li>Every change is audited.</li>
        </Bullets>
      </Section>

      <Section number={8} title="Unassigned Templates and Requests">
        <P>
          Everything made before OpCos existed has no OpCo and is <UI>Unassigned</UI>. Unassigned templates are only visible in the All OpCos view.
          A banner on the OpCos tab, “N unassigned requests have no OpCo”, links there.
        </P>
        <Table
          head={['Item', 'What you do']}
          rows={[
            ['Template', <span key="t">Open it and use <UI>Change OpCo</UI> (moves it, with a warning and <UI>Move anyway</UI> if requests would be affected) or <UI>Copy to OpCo</UI> (builds a separate analyzer for that OpCo).</span>],
            ['Request', <span key="r">Open it and use <UI>Assign</UI> or <UI>Move to OpCo</UI>. Its template has to be in the target OpCo first. Posted or delivered requests cannot be moved.</span>],
            ['New template', 'A template created inside an OpCo belongs to it. From All OpCos you choose its OpCo in the create dialog.'],
          ]}
        />
        <Callout kind="tip">Assign existing templates before you turn OpCos on for a client, or its users will see no templates.</Callout>
      </Section>

      <Section number={9} title="NetSuite and Business Central per OpCo">
        <Bullets>
          <li><span className="font-semibold">NetSuite:</span> in an OpCo’s Integrations card choose “This OpCo has its own NetSuite account” (account ID and secrets; saved secrets show “Saved — leave blank to keep”) or “Use the deployment’s NetSuite account”. Use <UI>Test connection</UI>, then <UI>Save connection</UI>.</li>
          <li><span className="font-semibold">Business Central:</span> add a connection with name, tenant ID, client ID, client secret, environment and company, mark a default, and use <UI>Test</UI>.</li>
          <li>An OpCo with no connection has no access to that ERP. It never falls back to another OpCo’s account.</li>
          <li>Only Super Admins set up the connections. The OpCo’s Admin does the NetSuite setup (record types and name mappings).</li>
        </Bullets>
      </Section>

      <Section number={10} title="Billing Split and Org-wide Settings">
        <Bullets>
          <li>Billing &amp; Analytics splits pages by OpCo (Super Admins only). Past months stay frozen, and moving a request changes only the open month.</li>
          <li>Branding, display preferences, custom domain and groups are organisation-wide and editable by any Admin. Per-OpCo things (ERP connections, a template’s group access) stay with Super Admins.</li>
          <li>Notification streams re-check membership every minute, so removing someone from an OpCo ends their live view of it.</li>
        </Bullets>
      </Section>
    </div>
  )
}
